import React, { useEffect, useState } from 'react';
import { message } from 'antd';
import { useProjectStore } from '../../hooks/useProjectStore';
import { fractureAPI } from '../../services/api';
import SimCompareChart, { type SimSnapshot } from './SimCompareChart';
import Comparison3DView from './Comparison3DView';
import SimulationComparisonCurves from './SimulationComparisonCurves';
import type { FracDesign, FracSimulation } from './types';
import './FracSimHistoryPage.css';

// /fracsim/history — 各压裂方案最新真实模拟结果对比
const TH = {
  bg: '#FFFFFF', text: '#1E2A3A', textSec: '#4F6680', textMuted: '#8598AD',
  accent: '#C2533A', accentLight: 'rgba(194,83,58,0.08)', ok: '#1E8449',
  headerBorder: '#D6DAE0', panelHeaderBg: '#F4F6F9',
  fontMono: '"Cascadia Code",Consolas,"SF Mono","Microsoft YaHei",monospace',
};
type ComparisonRow = { design: FracDesign; simulation: FracSimulation };

const numericSummary = (simulation: FracSimulation, ...keys: string[]): number | null => {
  const commercial = simulation.summary?.commercial_metrics;
  const sources = [simulation.summary, typeof commercial === 'object' && commercial ? commercial as Record<string, unknown> : {}];
  for (const source of sources) {
    for (const key of keys) {
      const value = Number(source[key]);
      if (Number.isFinite(value)) return value;
    }
  }
  return null;
};

export default function FracSimHistoryPage() {
  const { currentProject } = useProjectStore();
  const projectId = currentProject?.id;
  const [designs, setDesigns] = useState<FracDesign[]>([]);
  const [rows, setRows] = useState<ComparisonRow[]>([]);
  const [selectedSimulationIds, setSelectedSimulationIds] = useState<number[]>([]);
  const [comparisonStage, setComparisonStage] = useState<'WELL' | number>('WELL');
  const [loading, setLoading] = useState(true);
  const un = (d: any) => (Array.isArray(d) ? d : d?.results ?? []);

  useEffect(() => {
    if (!projectId) { setLoading(false); return; }
    setLoading(true);
    setRows([]);
    setSelectedSimulationIds([]);
    fractureAPI.listDesigns(projectId).then(async ({ data }) => {
      const ds: FracDesign[] = un(data);
      setDesigns(ds);
      const loaded = await Promise.allSettled(ds.map(async (design) => {
        const response = await fractureAPI.listSimulations({ design: design.id });
        const simulations: FracSimulation[] = un(response.data);
        return simulations
          .sort((left, right) => new Date(right.created_at).getTime() - new Date(left.created_at).getTime())
          .map((simulation) => ({ design, simulation } satisfies ComparisonRow));
      }));
      const available = loaded
        .filter((item): item is PromiseFulfilledResult<ComparisonRow[]> => item.status === 'fulfilled')
        .flatMap((item) => item.value)
        .sort((left, right) => new Date(right.simulation.created_at).getTime() - new Date(left.simulation.created_at).getTime());
      setRows(available);
      const latestPerDesign = available.filter((row, index, all) => all.findIndex((candidate) => candidate.design.id === row.design.id) === index);
      setSelectedSimulationIds(latestPerDesign.slice(0, 5).map((item) => item.simulation.id));
      if (loaded.some((item) => item.status === 'rejected')) message.warning('部分方案模拟结果加载失败');
    }).catch((error) => message.error(error?.message || '方案加载失败'))
      .finally(() => setLoading(false));
  }, [projectId]);

  const selectedRows = rows.filter((row) => selectedSimulationIds.includes(row.simulation.id));
  const metricValue = (simulation: FracSimulation) => {
    const score = Number(simulation.summary?.effectiveness_score);
    return Number.isFinite(score) && score > 0 ? score * 10000 + simulation.total_half_length : simulation.total_half_length;
  };
  const leadingSimulation = selectedRows.reduce<FracSimulation | null>((leading, row) => (
    !leading || metricValue(row.simulation) > metricValue(leading) ? row.simulation : leading
  ), null);

  // 对比图表快照
  const snapshots: SimSnapshot[] = selectedRows.map(({ design, simulation }) => ({
    id: simulation.id,
    label: design.name,
    half_length: simulation.total_half_length,
    avg_width: simulation.avg_width,
    avg_efficiency: simulation.avg_efficiency,
    frac_height: simulation.frac_height,
    effectiveness_score: Number(simulation.summary?.effectiveness_score ?? 0),
  }));
  const stageNumbers = Array.from(new Set(selectedRows.flatMap(({ simulation }) => (
    (simulation.results ?? []).map((stage) => stage.stage_number)
  )))).sort((left, right) => left - right);
  const mismatchMessages = (() => {
    if (selectedRows.length < 2) return [];
    const messages: string[] = [];
    if (new Set(selectedRows.map(({ simulation }) => simulation.model_type)).size > 1) messages.push('求解模型不一致');
    if (new Set(selectedRows.map(({ design }) => design.stage_count)).size > 1) messages.push('设计段数不一致');
    if (new Set(selectedRows.map(({ design }) => design.pump_rate ?? null)).size > 1) messages.push('设计排量不一致');
    if (new Set(selectedRows.map(({ design }) => design.total_fluid ?? null)).size > 1) messages.push('总液量不一致');
    if (new Set(selectedRows.map(({ design }) => design.total_proppant ?? null)).size > 1) messages.push('总加砂量不一致');
    if (comparisonStage !== 'WELL' && selectedRows.some(({ simulation }) => !simulation.results?.some((stage) => stage.stage_number === comparisonStage))) {
      messages.push(`部分模拟缺少第 ${comparisonStage} 段`);
    }
    return messages;
  })();
  const selectedSimulations = selectedRows.map((row) => row.simulation);

  if (!projectId) return <div style={{ padding: 40, color: TH.textSec }}>请先选择项目</div>;

  return (
    <div className="fracsim-history-page" style={{ height: '100vh', display: 'flex', flexDirection: 'column', background: TH.bg, fontFamily: 'inherit' }}>
      <div className="fracsim-history-header" style={{ padding: '8px 20px', borderBottom: `1px solid ${TH.headerBorder}`, display: 'flex', alignItems: 'center', gap: 12 }}>
        <h2 style={{ fontSize: 15, fontWeight: 700, color: TH.text, margin: 0 }}>方案对比</h2>
        <span style={{ fontSize: 12, color: TH.textMuted }}>{loading ? '加载中…' : `${rows.length} 条历史结果 / ${designs.length} 个方案`}</span>
      </div>
      <div style={{ flex: 1, overflow: 'auto' }}>
        {!loading && rows.length === 0 ? (
          <div style={{ padding: 50, textAlign: 'center', color: TH.textMuted, fontSize: 13 }}>
            <div style={{ fontSize: 28, opacity: 0.4 }}>○</div>
            当前压裂方案还没有可比较的模拟结果 — 请先运行裂缝扩展模拟
          </div>
        ) : (
          <>
            <div className="fracsim-history-picker" style={{ padding: '8px 16px', borderBottom: `1px solid ${TH.headerBorder}`, display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
              <strong style={{ fontSize: 11, color: TH.textSec }}>参与对比（最多5个）</strong>
              <label style={{ fontSize: 11, color: TH.textSec }}>范围&nbsp;
                <select value={comparisonStage} onChange={(event) => setComparisonStage(event.target.value === 'WELL' ? 'WELL' : Number(event.target.value))} style={{ fontSize: 11 }}>
                  <option value="WELL">全井</option>
                  {stageNumbers.map((stageNumber) => <option key={stageNumber} value={stageNumber}>第 {stageNumber} 段</option>)}
                </select>
              </label>
              {rows.map(({ design, simulation }, rowIndex) => {
                const checked = selectedSimulationIds.includes(simulation.id);
                const isLatest = rows.findIndex((candidate) => candidate.design.id === design.id) === rowIndex;
                return <label key={simulation.id} className={`fracsim-history-choice${checked ? ' is-checked' : ''}`} style={{ fontSize: 11, color: checked ? TH.text : TH.textMuted, cursor: 'pointer' }}>
                  <input type="checkbox" checked={checked} onChange={(event) => {
                    if (event.target.checked && selectedSimulationIds.length >= 5) {
                      message.warning('一次最多对比5个方案');
                      return;
                    }
                    setSelectedSimulationIds((current) => event.target.checked
                      ? [...current, simulation.id]
                      : current.filter((id) => id !== simulation.id));
                  }} /> {design.is_active ? '★ ' : ''}{design.name} · #{simulation.id}{isLatest ? '（最新）' : ''}
                </label>;
              })}
            </div>
            {mismatchMessages.length > 0 && <div style={{ padding: '6px 16px', color: '#A64B32', background: '#FFF7ED', borderBottom: `1px solid ${TH.headerBorder}`, fontSize: 11 }}>
              输入条件提示：{mismatchMessages.join('；')}。曲线和几何仍按各自已保存模拟结果展示，不会重新求解。
            </div>}
            {snapshots.length >= 2 && (
              <div style={{ padding: '8px 16px', borderBottom: `1px solid ${TH.headerBorder}` }}>
                <div style={{ fontSize: 11, fontWeight: 600, color: TH.textSec, marginBottom: 4 }}>所选历史模拟关键指标</div>
                <SimCompareChart sims={snapshots} height={200} />
              </div>
            )}
            {selectedSimulations.length > 0 && <div style={{ padding: '8px 16px', display: 'grid', gap: 8, borderBottom: `1px solid ${TH.headerBorder}` }}>
              <SimulationComparisonCurves simulations={selectedSimulations} stageNumber={comparisonStage === 'WELL' ? undefined : comparisonStage} />
              <Comparison3DView simulations={selectedSimulations} stageNumber={comparisonStage === 'WELL' ? undefined : comparisonStage} />
            </div>}
            <table className="fracsim-history-table" style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
            <thead>
              <tr style={{ background: TH.panelHeaderBg, position: 'sticky', top: 0 }}>
                {['方案 / 历史结果', '模拟时间', '模型', '施工排量(m³/min)', '总液量(m³)', '总砂量(t)', '总半缝长(m)', '有效支撑长(m)', '缝高(m)', '平均缝宽(mm)', '液效(%)', '铺置砂量(kg)', '导流能力', '有效性'].map((h) => (
                  <th key={h} style={{ padding: '6px 10px', textAlign: 'right', borderBottom: `1px solid ${TH.headerBorder}`, color: TH.textSec, fontWeight: 600, whiteSpace: 'nowrap' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {selectedRows.map(({ design, simulation }) => {
                const isLeading = leadingSimulation?.id === simulation.id && selectedRows.length > 1;
                return (
                  <tr key={simulation.id} className={isLeading ? 'is-leading' : ''} style={{ borderBottom: '1px solid #F0EDE4', background: isLeading ? TH.accentLight : 'transparent' }}>
                    <td style={{ ...td, textAlign: 'left', fontWeight: 700 }}>{design.name} · #{simulation.id}{isLeading && <span className="fracsim-history-leading">当前最高</span>}</td>
                    <td style={{ ...td, fontSize: 11, color: TH.textSec }}>{new Date(simulation.created_at).toLocaleString('zh-CN')}</td>
                    <td style={td}>{simulation.model_type}</td>
                    <td style={td}>{design.pump_rate ?? '—'}</td>
                    <td style={td}>{design.total_fluid ?? '—'}</td>
                    <td style={td}>{design.total_proppant ?? '—'}</td>
                    <td style={{ ...td, fontWeight: 700, color: TH.accent }}>{simulation.total_half_length}</td>
                    <td style={td}>{simulation.total_propped_length ?? '—'}</td>
                    <td style={td}>{simulation.frac_height}</td>
                    <td style={td}>{simulation.avg_width}</td>
                    <td style={td}>{simulation.avg_efficiency}</td>
                    <td style={td}>{numericSummary(simulation, 'deposited_proppant_kg', 'total_deposited_proppant_kg') ?? '—'}</td>
                    <td style={td}>{numericSummary(simulation, 'conductivity_md_ft', 'avg_conductivity_md_ft', 'effective_conductivity') ?? '—'}</td>
                    <td style={td}>{Number(simulation.summary?.effectiveness_score ?? 0) || '—'}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          </>
        )}
      </div>
    </div>
  );
}

const td: React.CSSProperties = { padding: '5px 10px', textAlign: 'right', fontFamily: TH.fontMono, color: TH.text };
